/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo, useState, useRef } from 'react'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import moment from 'moment'
import { css } from '@emotion/react'

import { useLazyGetLabNotesQuery } from 'store/services/notes'
import { Note } from 'store/types'

interface LazyNotesPopoverProps {
  orderId: number
  children: ReactNode
}

const HIDE_DELAY_MS = 1000

export const LazyNotesPopover = ({ orderId, children }: LazyNotesPopoverProps) => {
  const [trigger, { data, isFetching, isUninitialized }] = useLazyGetLabNotesQuery()
  const [loaded, setLoaded] = useState(false)
  const [show, setShow] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const targetRef = useRef<HTMLSpanElement>(null)

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      setShow(false)
    }, HIDE_DELAY_MS)
  }, [clearHideTimeout])

  const handleMouseEnter = useCallback(() => {
    clearHideTimeout()
    setShow(true)
    if (!loaded && (isUninitialized || !data)) {
      trigger({ orderId: String(orderId) })
      setLoaded(true)
    }
  }, [clearHideTimeout, loaded, isUninitialized, data, orderId, trigger])

  const handleMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const handlePopoverMouseEnter = useCallback(() => {
    clearHideTimeout()
  }, [clearHideTimeout])

  const handlePopoverMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const content = useMemo(() => {
    if (isFetching || !data) {
      return (
        <div className="p-2">
          <small>Loading…</small>
        </div>
      )
    }

    const notes: Note[] = data || []

    // Sort notes in reverse chronological order (newest first)
    const sortedNotes = [...notes].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return (
      <div 
        className="p-2"
        css={css`
          max-height: 60vh;
          overflow-y: auto;
        `}
      >
        {sortedNotes.map((n) => (
          <p
            key={n.uuid}
            className="p-2 mb-3"
            css={css`
              background-color: lightyellow;
              border: 1px solid #ffc10780;
            `}
          >
            {moment(n.createdAt).format('MM/DD/YYYY HH:mm a')}:<br /> {n.text}
          </p>
        ))}
      </div>
    )
  }, [data, isFetching])

  const popover = (
    <Popover
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
    >
      <Popover.Header as="h3">Recent Notes</Popover.Header>
      {content}
    </Popover>
  )

  return (
    <>
      <span 
        ref={targetRef}
        onMouseEnter={handleMouseEnter} 
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter} 
        onBlur={handleMouseLeave}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      <Overlay
        show={show}
        target={targetRef.current}
        placement="left"
        transition={false}
      >
        {popover}
      </Overlay>
    </>
  )
}


